/**
 * Polly API client
 *
 * A Polly is the small, fun, cute sibling of a LIQUIDO poll: no team, no registration,
 * no login screen. You type a question and some options, tap your passkey, and share one link.
 *
 * This module is deliberately self-contained.
 *
 *   IT MUST NOT IMPORT liquido-graphql-client.js - and nothing outside src/polly/ may import it.
 *
 * That rule is the whole point. The first attempt at Polly reused the team client, and a polly
 * promptly leaked into the team's poll list through the shared pollsCache. Sharing bought little
 * and cost a discriminator field, a cache filter and two authorization regimes on one row.
 * So: own transport, own session, own state. The only thing Polly shares with LIQUIDO polls is
 * the winner algorithm, and that lives in the backend.
 */

import axios from "axios"
import config from "config"
import { PollyError, POLLY_STATUS } from "./polly-constants.js"
import { getSessionJwt, hasSession, hasPasskeySession, isGuestSession, setSession, clearSession, isSessionExpired, sessionRejected } from "./polly-session.js"
import { createSessionRenewal, isOlderThan } from "@/shared/services/session-renewal.js"
import { pollyQueryMock, claimPollyLinkMail, credentialOfPollyJwt } from "../../../mock-backend/liquido-mock-polly.js"

const GRAPHQL = "/graphql"

/**
 * Our own axios instance.
 *
 * liquido-graphql-client.js mutates the *global* axios defaults (baseURL, Authorization header)
 * and installs global interceptors. Creating our own instance and attaching the polly JWT
 * per request keeps the two sessions from ever seeing each other's headers.
 *
 * Its baseURL follows the team client's rule (liquido-graphql-client.js): with the mock backend on,
 * the dev server this page came from answers, so requests go to the page's own origin - whatever
 * LIQUIDO_API_URL says.
 */
const http = axios.create({
	baseURL: (config.mockBackend && config.configSource !== "test") ? "" : config.LIQUIDO_API_URL,
	timeout: 10000,
})

http.interceptors.request.use(req => {
	// Never send a session that has run out: the backend would answer this request - any request, even reading a
	// polly - with HTTP 401. sessionRejected() drops it; a passkey user is then sent to the login page.
	if (isSessionExpired()) sessionRejected()
	const jwt = getSessionJwt()
	if (jwt) req.headers.Authorization = "Bearer " + jwt
	return req
})

/**
 * vitest only: there is no dev server to answer HTTP, so the polly mock is called in-process - the
 * same split as in liquido-graphql-client.js. In the e2e mock mode requests go over HTTP to the dev
 * server, which answers them with that same mock (mock-backend/liquido-mock-http-server.js).
 */
const isMockInProcess = () => config.mockBackend === true && config.configSource === "test"

/** What we ask the backend to return for a polly. */
const POLLY_FIELDS = `{
	publicId
	title
	status
	createdAt
	votingEndAt
	ownerName
	numBallots
	voterNames
	isOwner
	alreadyVoted
	proposals { id title }
	winner { id title }
}`

/**
 * Sliding renewal (src/shared/services/session-renewal.js): before a request, once the polly session is more than an
 * hour old, mutation renewSession gets a fresh one - beside the request, which does not wait. Every page load sends a
 * request (the backend's limits, polly-app.vue), so every visit gives the full 14 days again; a passkey session ends
 * 90 days after its passkey tap all the same (the backend decides). A guest stays a guest: the flag belongs to this very
 * token, which the renewal only replaces while it is still the session.
 */
const sessionRenewal = createSessionRenewal({
	getJwt: getSessionJwt,
	setJwt: jwt => setSession(jwt, { guest: isGuestSession() }),
	renew: () => pollyApi.renewSession(),
	isDue: isOlderThan(60 * 60),
})

/**
 * Send a GraphQL operation to the polly backend.
 * Rejects with { pollyErrorCode, msg } so callers can branch on a stable code.
 */
async function pollyQuery(query, variables) {
	sessionRenewal.renewIfDue()   // not awaited: the request goes out with the current session, which is still valid
	return sendPolly(query, variables)
}

/** pollyQuery() without asking for a renewal - for renewSession() itself */
async function sendPolly(query, variables) {
	if (isMockInProcess()) return pollyQueryMock(query, variables, getSessionJwt())

	return http.post(GRAPHQL, { query, variables })
		.then(res => {
			// GraphQL answers HTTP 200 even for errors and puts them in an `errors` array
			if (res.data?.errors?.length > 0) {
				const first = res.data.errors[0]
				return Promise.reject({
					pollyErrorCode: first.extensions?.pollyErrorCode,
					msg: first.message,
				})
			}
			return res.data
		})
		.catch(err => {
			if (err?.pollyErrorCode !== undefined) return Promise.reject(err)	// already shaped above
			// HTTP 401 with no polly error code of its own: the backend refused the session itself (expired, or signed
			// with another key) before any resolver ran. Drop it, and a passkey user logs in again (polly-login.vue).
			if (err?.response?.status === 401 && !err.response.data?.pollyErrorCode) {
				console.warn("polly-client: the backend refused this browser's polly session")
				sessionRejected()
				return Promise.reject({ pollyErrorCode: PollyError.NEED_PASSKEY, msg: "Polly session refused" })
			}
			console.error("polly-client: request failed", err)
			return Promise.reject({ pollyErrorCode: undefined, msg: err?.message || "Network error" })
		})
}

const pollyApi = {

	PollyError,
	POLLY_STATUS,

	hasSession,
	hasPasskeySession,
	isGuestSession,
	setSession,
	clearSession,
	getSessionJwt,

	/**
	 * The one link the admin shares. Everybody opens the same URL - there is no admin link.
	 * It is this app's own address: whatever host the Polly app is served from (polly.liquido.vote, a dev server).
	 */
	shareLinkFor(publicId) {
		return window.location.origin + "/polly/" + publicId
	},

	/**
	 * The backend's limits that Polly checks too, merged over config.common.js at the start (polly-app.vue).
	 * `query liquidoConfig` needs no session; this asks only for the fields Polly uses.
	 */
	async getConfig() {
		const query = `query { liquidoConfig { usernameMinLength pollTitleMinLength pollyNameMaxLength } }`
		return pollyQuery(query).then(res => res.data.liquidoConfig)
	},

	/**
	 * Create a polly, from step 2 of the creating wizard (polly-page.vue). It is open for voting immediately and runs until
	 * its owner finishes it. Requires a passkey session; the caller becomes the owner.
	 *
	 * @param {String} title the question
	 * @param {Array} proposalTitles the options, in the order they were typed
	 * @param {String} ownerName the creator's (nick)name, shown to the friends
	 * @param {String} ownerEmail the share link is mailed there once (sendLinkMail). Shown to nobody. Ignored
	 *        by the backend once the passkey belongs to an account: the account's address is used.
	 */
	async createPolly(title, proposalTitles, ownerName, ownerEmail) {
		const query = `mutation createPolly($title: String!, $proposalTitles: [String!]!, $ownerName: String!, $ownerEmail: String!) {
			createPolly(title: $title, proposalTitles: $proposalTitles, ownerName: $ownerName, ownerEmail: $ownerEmail) ${POLLY_FIELDS}
		}`
		return pollyQuery(query, {
			title: (title || "").trim(),
			proposalTitles,
			ownerName: (ownerName || "").trim(),
			ownerEmail: (ownerEmail || "").trim(),
		}).then(res => res.data.createPolly)
	},

	/**
	 * Ask the backend to mail the share link to the address given on the start page. Owner only, and
	 * only once per polly - a second call sends nothing. A REST call rather than part of createPolly:
	 * the backend's mailer cannot run inside a GraphQL mutation (PollyMailRestApi).
	 */
	async sendLinkMail(publicId) {
		if (isMockInProcess()) return claimPollyLinkMail(publicId, credentialOfPollyJwt(getSessionJwt()))
		return http.post("/polly/" + encodeURIComponent(publicId) + "/link-mail").then(res => res.data)
	},

	/**
	 * Read a polly by its public id.
	 * Works without a session (a friend just opened the link). With a session the answer
	 * also says whether this visitor owns it and whether they already voted.
	 */
	async getPolly(publicId) {
		const query = `query polly($publicId: String!) { polly(publicId: $publicId) ${POLLY_FIELDS} }`
		return pollyQuery(query, { publicId }).then(res => res.data.polly)
	},

	/** Change title or options. Owner only, and only while nobody has voted. */
	async editPolly(publicId, title, proposalTitles) {
		const query = `mutation editPolly($publicId: String!, $title: String!, $proposalTitles: [String!]!) {
			editPolly(publicId: $publicId, title: $title, proposalTitles: $proposalTitles) ${POLLY_FIELDS}
		}`
		return pollyQuery(query, { publicId, title: (title || "").trim(), proposalTitles })
			.then(res => res.data.editPolly)
	},

	/**
	 * Cast a ballot: the option ids in the voter's preferred order, favourite first.
	 *
	 * No voterToken. The passkey session identifies the voter, and the backend enforces
	 * one ballot per (polly, credential) with a unique constraint. That makes a polly ballot
	 * pseudonymous rather than truly anonymous - a deliberate trade for a poll among friends.
	 * A LIQUIDO team poll keeps the voterToken indirection and stays fully anonymous.
	 *
	 * voteOrder is declared [String!]!, not [ID!]!. The backend cannot offer an ID scalar here:
	 * MicroProfile's @Id applies to the outer type before SmallRye unwraps the collection, so a
	 * list of IDs is inexpressible, and the generated schema has no ID type at all. GraphQL
	 * requires a variable's named type to match the argument's exactly, so [ID!]! is rejected
	 * before any resolver runs. The values on the wire are identical either way.
	 *
	 * @param {String} voterName shown to everybody in the list of who already voted - but stored apart
	 *        from the ballot. Each name votes once per polly (VOTER_NAME_TAKEN).
	 */
	async voteInPolly(publicId, voteOrder, voterName) {
		const query = `mutation voteInPolly($publicId: String!, $voteOrder: [String!]!, $voterName: String!) {
			voteInPolly(publicId: $publicId, voteOrder: $voteOrder, voterName: $voterName) ${POLLY_FIELDS}
		}`
		return pollyQuery(query, { publicId, voteOrder: (voteOrder || []).map(String), voterName: (voterName || "").trim() })
			.then(res => res.data.voteInPolly)
	},

	/** Close the polly and calculate the winner (Ranked Pairs, shared with LIQUIDO polls). Owner only. */
	async finishPolly(publicId) {
		const query = `mutation finishPolly($publicId: String!) { finishPolly(publicId: $publicId) ${POLLY_FIELDS} }`
		return pollyQuery(query, { publicId }).then(res => res.data.finishPolly)
	},

	/**
	 * Make sure this browser may vote: a session of any kind - or, without one, a guest session.
	 * A friend votes without a passkey; the passkey is offered afterwards, never required.
	 */
	/**
	 * A fresh token for this browser's polly session, with its full 14 days again. Called by the sliding renewal
	 * (sessionRenewal above), not by the pages.
	 * @returns {Promise<String>} the fresh token, or null when the backend refused to renew it (a passkey that is gone,
	 *   or the session at its maximum) - it then still works until it runs out. Rejects when the backend cannot be reached.
	 */
	async renewSession() {
		return sendPolly(`mutation renewSession { renewSession }`)
			.then(res => res.data.renewSession)
			.catch(err => (err?.pollyErrorCode !== undefined ? null : Promise.reject(err)))
	},

	async ensureVoterSession() {
		if (hasSession()) return
		const res = await pollyQuery(`mutation pollyGuestSession { pollyGuestSession }`)
		setSession(res.data.pollyGuestSession, { guest: true })
	},

	/**
	 * Who this passkey's owner is, to fill in the start page again: the account's name and email once the
	 * passkey belongs to one (hasAccount - the email is then fixed), else the ones last given on the start
	 * page, not confirmed yet. Passkey sessions only. Name and email may be null.
	 *
	 * @returns {Promise<{ name, email, hasAccount: Boolean, emailConfirmed: Boolean }>}
	 */
	async pollyProfile() {
		return pollyQuery(`query pollyProfile { pollyProfile { name email hasAccount emailConfirmed } }`).then(res => res.data.pollyProfile)
	},

	/**
	 * Confirm the creator's email with the token from the link in the polly's link mail. Only with the
	 * passkey the polly was started with (WRONG_PASSKEY otherwise); that passkey then belongs to the
	 * account with this address. Errors: NEED_PASSKEY, WRONG_PASSKEY, EMAIL_CONFIRMATION_INVALID,
	 * EMAIL_CONFIRMATION_EXPIRED, ACCOUNT_EMAIL_UNVERIFIED.
	 *
	 * @param {String} token from the link
	 * @returns {Promise<{ alreadyConfirmed: Boolean, profile: { name, email, hasAccount, emailConfirmed } }>}
	 */
	async confirmEmail(token) {
		const query = `mutation confirmPollyEmail($token: String!) {
			confirmPollyEmail(token: $token) { alreadyConfirmed profile { name email hasAccount emailConfirmed } }
		}`
		return pollyQuery(query, { token: token || "" }).then(res => res.data.confirmPollyEmail)
	},

	/**
	 * Every polly this passkey created.
	 * This is why a polly needs no email: the passkey is both the login and the bookmark.
	 */
	async myPollys() {
		const query = `query myPollys { myPollys ${POLLY_FIELDS} }`
		return pollyQuery(query).then(res => res.data.myPollys || [])
	},
}

export default pollyApi
